import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { t } from 'i18next';

import {
  FORM_TABLE_COLUMN_NAME,
  FORM_TABLE_FIRST_COLUMN_NAME,
} from 'components/_basic/Form/FormTableComponent/constants';

import { getAdditionalColumnProperties } from '../../helpers';

export const FIRST_COLUMN_WIDTH = 342;
export const FIRST_COLUMN_MAXLENGTH = 150;
export const MAX_FIELD_LENGTH = 2;

export const GENERAL_AND_ADMINISTRATIVE_FORM_NAMES = {
  GENERAL_AND_ADMINISTRATIVE_CURRENCY: 'generalAndAdministrativeCurrency',
  GENERAL_AND_ADMINISTRATIVE_TABLE: 'generalAndAdministrativeTable',
  GENERAL_AND_ADMINISTRATIVE_TABLE_TOTAL: 'totalGeneralAndAdministrativeTable',
} as const;

export const useGetItemsColumnDefaultValues = () => {
  const { t, i18n } = useTranslation();

  return useMemo(
    () => [
      'Salaries and wages for executive, administrative, and support staff',
      'Benefits and payroll taxes for administrative staff',
      'Depreciation of fixed assets',
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.otherTaxes'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.trainingAndCertificationOfStaff'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.officeSuppliesAndEquipment'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.utilitiesForOfficeSpaces'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.rentForOfficeSpaces'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.professionalFeesLegalAuditConsulting'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.insuranceCosts'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.travelExpenses'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.itAndSoftwareExpensesForInternalUse'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.communicationServices'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.internetExpenses'),
      t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.other'),
    ],
    [t, i18n],
  );
};

export const itemsColumnDefaultValues = [
  'Salaries and wages for executive, administrative, and support staff',
  'Benefits and payroll taxes for administrative staff',
  'Depreciation of fixed assets',
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.otherTaxes'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.trainingAndCertificationOfStaff'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.officeSuppliesAndEquipment'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.utilitiesForOfficeSpaces'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.rentForOfficeSpaces'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.professionalFeesLegalAuditConsulting'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.insuranceCosts'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.travelExpenses'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.itAndSoftwareExpensesForInternalUse'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.communicationServices'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.internetExpenses'),
  t('pdfPages.mappings.financialPlan.generalAndAdministrativeForm.other'),
];

export const generalAndAdministrativeColumns = [
  {
    title: 'financialPlan.plannedExpenses.generalAndAdministrative.columnTitles.plannedMonthlyExpenses',
    id: 'financialPlan.plannedExpenses.generalAndAdministrative.columnTitles.plannedMonthlyExpenses',
  },
  {
    title: 'financialPlan.plannedExpenses.generalAndAdministrative.columnTitles.annualExpenseGrowthRate',
    id: 'financialPlan.plannedExpenses.generalAndAdministrative.columnTitles.annualExpenseGrowthRate',
  },
];

const additionalProperties: Record<string, string> = getAdditionalColumnProperties(
  FORM_TABLE_COLUMN_NAME,
  MAX_FIELD_LENGTH,
);

export const defaultGeneralAndAdministrativeItem = { [FORM_TABLE_FIRST_COLUMN_NAME]: '', ...additionalProperties };

export const getDefaultGeneralAndAdministrativeRows = (defaultValues: string[]) =>
  defaultValues.map((item) => {
    return { [FORM_TABLE_FIRST_COLUMN_NAME]: item, ...additionalProperties };
  });

export type IRow = Record<string, string | number | undefined>;

export interface IGeneralAndAdministrativeValuesType {
  [GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_CURRENCY]: string;
  [GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_TABLE]: IRow[];
  [GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_TABLE_TOTAL]: IRow;
}

export const useGetDefaultGeneralAndAdministrativeRows = () => {
  const defaultValues = useGetItemsColumnDefaultValues();

  return useMemo(() => getDefaultGeneralAndAdministrativeRows(defaultValues), [defaultValues]);
};

export const useGetGeneralAndAdministrativeInitialValues = (): IGeneralAndAdministrativeValuesType => {
  const defaultRows = useGetDefaultGeneralAndAdministrativeRows();

  return useMemo(
    () => ({
      [GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_CURRENCY]: DIMENSIONALITY_VALUES.THOUSAND,
      [GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_TABLE]: defaultRows,
      [GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_TABLE_TOTAL]:
        defaultGeneralAndAdministrativeItem,
    }),
    [defaultRows],
  );
};

export const generalAndAdministrativeInitialValues = {
  [GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_CURRENCY]: DIMENSIONALITY_VALUES.THOUSAND,
  [GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_TABLE]:
    getDefaultGeneralAndAdministrativeRows(itemsColumnDefaultValues),
  [GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_TABLE_TOTAL]: defaultGeneralAndAdministrativeItem,
};
